import { TaskPriority, TaskRepeat } from "../../services/taskService";
import { addDays, daysBetween, weekdayOf } from "./taskUtils";

/**
 * Quick add: one line, understood as you type.
 *
 *   "Call customer about ORD202610260013 @Rahim tomorrow 4pm !high #delivery"
 *
 * → title "Call customer about ORD202610260013", assigned to Rahim, due
 *   tomorrow at 4 pm, High priority, label "delivery", order linked.
 *
 * Pure: everything it needs (people, today) is passed in, so it's tested
 * without a browser. Dates are calendar days ("YYYY-MM-DD").
 */

export interface QuickAddPerson {
    user_id: string;
    name: string;
    email: string;
}

export type ParsedPartKind = "assignee" | "unknown-person" | "due" | "time" | "priority" | "label" | "order" | "phone" | "repeat";

export interface ParsedPart {
    kind: ParsedPartKind;
    /** The words typed, as typed. */
    text: string;
}

export interface ParsedTask {
    title: string;
    /** undefined = not mentioned; a user id when "@name" matched someone. */
    assigneeId?: string;
    /** "@xyz" that matched nobody. */
    unknownPerson?: string;
    dueDate?: string;
    /** "HH:MM:00" */
    dueTime?: string;
    priority?: TaskPriority;
    labels: string[];
    orderNumber?: string;
    /** 11-digit local mobile number, e.g. "01712345678". */
    phone?: string;
    repeat?: { rule: TaskRepeat; interval: number };
    parts: ParsedPart[];
}

interface Context {
    today: string;
    people: QuickAddPerson[];
    meId?: string;
}

const WEEKDAYS: Record<string, number> = {
    sunday: 0, sun: 0,
    monday: 1, mon: 1,
    tuesday: 2, tues: 2, tue: 2,
    wednesday: 3, wed: 3,
    thursday: 4, thurs: 4, thu: 4,
    friday: 5, fri: 5,
    saturday: 6, sat: 6,
};
// Short forms that are also everyday words only count after "on", "by", …
const AMBIGUOUS_WEEKDAYS = new Set(["sun", "sat", "wed"]);
const WEEKDAY_WORDS = "sunday|monday|tuesday|wednesday|thursday|friday|saturday|tues|thurs|sun|mon|tue|wed|thu|fri|sat";

const MONTHS: Record<string, number> = {
    jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12,
};
const MONTH_WORDS = "january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sept|sep|oct|nov|dec";

const NUMBER_WORDS: Record<string, number> = { a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10 };

const PRIORITY_WORDS: Record<string, TaskPriority> = {
    urgent: "URGENT", u: "URGENT",
    high: "HIGH", h: "HIGH",
    normal: "NORMAL", n: "NORMAL",
    low: "LOW", l: "LOW",
};

// "on", "by", "due" (and "at" for times) read as part of the date.
const LEAD = "(?:(?:on|by|due|before)\\s+)?";

const pad = (n: number) => String(n).padStart(2, "0");

function validDay(y: number, m: number, d: number): string | null {
    if (m < 1 || m > 12 || d < 1) return null;
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    if (d > last) return null;
    return `${y}-${pad(m)}-${pad(d)}`;
}

/** The coming `weekday` after today (1–7 days ahead). */
export function comingWeekday(today: string, weekday: number, allowToday = false): string {
    const diff = (weekday - weekdayOf(today) + 7) % 7;
    return addDays(today, diff === 0 && !allowToday ? 7 : diff);
}

function addMonths(day: string, months: number): string {
    const [y, m, d] = day.split("-").map(Number);
    const total = m - 1 + months;
    const year = y + Math.floor(total / 12);
    const month = (total % 12) + 1;
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return `${year}-${pad(month)}-${pad(Math.min(d, last))}`;
}

/** A day and month with no year: this year, or next year if it's passed. */
function upcoming(today: string, month: number, day: number, year?: number): string | null {
    if (year) return validDay(year < 100 ? 2000 + year : year, month, day);
    const thisYear = Number(today.slice(0, 4));
    const candidate = validDay(thisYear, month, day);
    if (!candidate) return validDay(thisYear + 1, month, day);
    return daysBetween(today, candidate) < 0 ? validDay(thisYear + 1, month, day) : candidate;
}

/** Match a person by "@text": "me", first name, any name part, or email. */
export function matchPerson(query: string, people: QuickAddPerson[], meId?: string): QuickAddPerson | undefined {
    const q = query.toLowerCase().replace(/[.,;:!?]+$/, "");
    if (!q) return undefined;
    if (q === "me" && meId) return people.find((p) => p.user_id === meId);
    const parts = (p: QuickAddPerson) => p.name.toLowerCase().split(/\s+/).filter(Boolean);
    const local = (p: QuickAddPerson) => p.email.toLowerCase().split("@")[0];
    const squash = (s: string) => s.replace(/[\s._-]+/g, "");
    return (
        people.find((p) => parts(p)[0] === q) ||
        people.find((p) => squash(p.name.toLowerCase()) === squash(q)) ||
        people.find((p) => parts(p)[0]?.startsWith(q)) ||
        people.find((p) => parts(p).some((w) => w.startsWith(q))) ||
        people.find((p) => local(p).startsWith(q) || squash(local(p)).startsWith(squash(q)))
    );
}

export function parseQuickAdd(input: string, { today, people, meId }: Context): ParsedTask {
    const text = input;
    const taken: [number, number][] = [];
    const parts: (ParsedPart & { at: number })[] = [];
    const result: ParsedTask = { title: "", labels: [], parts: [] };

    const free = (start: number, end: number) => taken.every(([s, e]) => end <= s || start >= e);

    /** Run `pattern` over the line; `handle` returns true to claim the match. */
    const scan = (pattern: RegExp, handle: (m: RegExpExecArray, start: number, end: number) => boolean | "keep") => {
        pattern.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = pattern.exec(text))) {
            // Leading separator groups (start of line / space) aren't part of the token.
            const lead = m[1] !== undefined && /^\s*$/.test(m[1]) ? m[1].length : 0;
            const start = m.index + lead;
            const end = m.index + m[0].length;
            if (m[0].length === 0) {
                pattern.lastIndex++;
                continue;
            }
            if (!free(start, end)) continue;
            const outcome = handle(m, start, end);
            if (outcome === true) taken.push([start, end]);
        }
    };
    const note = (kind: ParsedPartKind, start: number, end: number) =>
        parts.push({ kind, text: text.slice(start, end).trim(), at: start });

    // ── Repeats (before weekdays: "every monday") ───────────────────────────
    scan(new RegExp(`(^|\\s)(?:every\\s+(\\d+|other)\\s+(day|week|month)s?|every\\s+(day|week|month)|(daily|weekly|monthly)|every\\s+(${WEEKDAY_WORDS}))\\b`, "gi"), (m, s, e) => {
        if (result.repeat) return false;
        const unit = (m[3] || m[4] || m[5] || "").toLowerCase();
        const interval = m[2] ? (m[2].toLowerCase() === "other" ? 2 : Math.min(365, Number(m[2]) || 1)) : 1;
        if (m[6]) {
            result.repeat = { rule: "WEEKLY", interval: 1 };
            result.dueDate = comingWeekday(today, WEEKDAYS[m[6].toLowerCase()], true);
        } else {
            const rule: TaskRepeat = unit.startsWith("day") || unit === "daily" ? "DAILY" : unit.startsWith("week") ? "WEEKLY" : "MONTHLY";
            result.repeat = { rule, interval };
        }
        note("repeat", s, e);
        return true;
    });

    // ── Dates ───────────────────────────────────────────────────────────────
    const setDue = (day: string | null, s: number, e: number) => {
        if (!day || result.dueDate) return false;
        result.dueDate = day;
        note("due", s, e);
        return true;
    };

    scan(new RegExp(`(^|\\s)${LEAD}(today|tonight|tomorrow|tmrw|tmr)\\b`, "gi"), (m, s, e) => {
        const word = m[2].toLowerCase();
        return setDue(word === "today" || word === "tonight" ? today : addDays(today, 1), s, e);
    });

    scan(/(^|\s)(?:(?:on|by|due|before)\s+)?next\s+week\b/gi, (_m, s, e) => setDue(comingWeekday(today, 1), s, e));

    scan(new RegExp(`(^|\\s)(?:in\\s+)(\\d+|a|an|one|two|three|four|five|six|seven|eight|nine|ten)\\s+(day|week|month)s?\\b`, "gi"), (m, s, e) => {
        const n = NUMBER_WORDS[m[2].toLowerCase()] ?? Number(m[2]);
        const unit = m[3].toLowerCase();
        const day = unit === "day" ? addDays(today, n) : unit === "week" ? addDays(today, 7 * n) : addMonths(today, n);
        return setDue(day, s, e);
    });

    scan(new RegExp(`(^|\\s)((?:on|by|due|before|next|this)\\s+)?(${WEEKDAY_WORDS})\\b`, "gi"), (m, s, e) => {
        const word = m[3].toLowerCase();
        if (AMBIGUOUS_WEEKDAYS.has(word) && !m[2]) return false;
        return setDue(comingWeekday(today, WEEKDAYS[word]), s, e);
    });

    scan(new RegExp(`(^|\\s)${LEAD}(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MONTH_WORDS})\\.?(?:,?\\s+(\\d{4}))?\\b`, "gi"), (m, s, e) =>
        setDue(upcoming(today, MONTHS[m[3].toLowerCase().slice(0, 3)], Number(m[2]), m[4] ? Number(m[4]) : undefined), s, e),
    );
    scan(new RegExp(`(^|\\s)${LEAD}(${MONTH_WORDS})\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s+(\\d{4}))?\\b`, "gi"), (m, s, e) =>
        setDue(upcoming(today, MONTHS[m[2].toLowerCase().slice(0, 3)], Number(m[3]), m[4] ? Number(m[4]) : undefined), s, e),
    );
    // 2026-10-25
    scan(new RegExp(`(^|\\s)${LEAD}(\\d{4})-(\\d{2})-(\\d{2})\\b`, "g"), (m, s, e) => setDue(validDay(Number(m[2]), Number(m[3]), Number(m[4])), s, e));
    // 25/10 or 25/10/2026 — day first, as written here.
    scan(new RegExp(`(^|\\s)${LEAD}(\\d{1,2})/(\\d{1,2})(?:/(\\d{2,4}))?(?=$|[\\s,.;!?])`, "g"), (m, s, e) =>
        setDue(upcoming(today, Number(m[3]), Number(m[2]), m[4] ? Number(m[4]) : undefined), s, e),
    );

    // ── Times ───────────────────────────────────────────────────────────────
    const setTime = (h: number, min: number, s: number, e: number) => {
        if (result.dueTime || h > 23 || min > 59) return false;
        result.dueTime = `${pad(h)}:${pad(min)}:00`;
        note("time", s, e);
        return true;
    };
    scan(/(^|\s)(?:at\s+|by\s+)?(\d{1,2})(?::(\d{2}))?\s?(am|pm)\b/gi, (m, s, e) => {
        let h = Number(m[2]);
        if (h < 1 || h > 12) return false;
        const pm = m[4].toLowerCase() === "pm";
        if (pm && h !== 12) h += 12;
        if (!pm && h === 12) h = 0;
        return setTime(h, Number(m[3] || 0), s, e);
    });
    scan(/(^|\s)(?:at\s+|by\s+)?([01]?\d|2[0-3]):([0-5]\d)\b/g, (m, s, e) => setTime(Number(m[2]), Number(m[3]), s, e));
    scan(/(^|\s)(noon|midday)\b/gi, (_m, s, e) => setTime(12, 0, s, e));

    // ── Priority ────────────────────────────────────────────────────────────
    scan(/(^|\s)!(urgent|high|normal|low|u|h|n|l)\b/gi, (m, s, e) => {
        if (result.priority) return false;
        result.priority = PRIORITY_WORDS[m[2].toLowerCase()];
        note("priority", s, e);
        return true;
    });
    scan(/(^|\s)(!{2,3})(?=$|\s)/g, (m, s, e) => {
        if (result.priority) return false;
        result.priority = m[2].length === 3 ? "URGENT" : "HIGH";
        note("priority", s, e);
        return true;
    });

    // ── Person ──────────────────────────────────────────────────────────────
    scan(/(^|\s)@([^\s@]+)/g, (m, s, e) => {
        if (result.assigneeId || result.unknownPerson) return false;
        const person = matchPerson(m[2], people, meId);
        if (person) {
            result.assigneeId = person.user_id;
            note("assignee", s, e);
        } else {
            result.unknownPerson = m[2];
            note("unknown-person", s, e);
        }
        return true;
    });

    // ── Labels ──────────────────────────────────────────────────────────────
    scan(/(^|\s)#([^\s#@!,.;:?]{1,30})/g, (m, s, e) => {
        if (/^\d+$/.test(m[2])) return false; // "#3" reads as a number, not a label
        if (!result.labels.some((l) => l.toLowerCase() === m[2].toLowerCase()) && result.labels.length < 10) {
            result.labels.push(m[2]);
            note("label", s, e);
        }
        return true;
    });

    // ── Linked records (kept in the title — they're part of the sentence) ──
    scan(/(^|[^\w])(ORD\d{6,})\b/gi, (m, _s, e) => {
        if (!result.orderNumber) {
            result.orderNumber = m[2].toUpperCase();
            note("order", m.index + m[1].length, e);
        }
        return "keep";
    });
    scan(/(^|\D)((?:\+?88)?01[3-9]\d{8})(?!\d)/g, (m, _s, e) => {
        if (!result.phone) {
            result.phone = m[2].replace(/^\+?88/, "");
            note("phone", m.index + m[1].length, e);
        }
        return "keep";
    });

    // A repeat with no date starts today (or on its weekday).
    if (result.repeat && !result.dueDate) result.dueDate = today;

    // ── Title: what's left ──────────────────────────────────────────────────
    let title = "";
    let cursor = 0;
    [...taken].sort((a, b) => a[0] - b[0]).forEach(([s, e]) => {
        title += text.slice(cursor, s) + " ";
        cursor = e;
    });
    title += text.slice(cursor);
    result.title = title
        .replace(/\s+/g, " ")
        .replace(/\s+([,.;:!?])/g, "$1")
        .replace(/^[\s,;:–—-]+|[\s,;:–—-]+$/g, "")
        .trim();
    result.parts = parts.sort((a, b) => a.at - b.at).map(({ at, ...p }) => p);
    return result;
}
